import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import NavBar from "@/components/NavBar";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { ASSETS } from "@/lib/assets";
import { Skull, Crown, Map, Shield, Loader, ChevronLeft } from "lucide-react";
import { toast } from "sonner";

export default function NewGame() {
  const { refreshState } = useAuth();
  const navigate = useNavigate();
  const [permadeath, setPermadeath] = useState(true);
  const [mapSize, setMapSize] = useState(24);
  const [quartermaster, setQuartermaster] = useState(false);
  const [lenience, setLenience] = useState(0.5);  // 0=strict, 1=lenient
  const [busy, setBusy] = useState(false);
  const [meta, setMeta] = useState(null);

  useEffect(() => {
    api.get("/game/meta").then(({ data }) => setMeta(data));
  }, []);

  const start = async () => {
    setBusy(true);
    try {
      await api.post("/game/new", {
        permadeath, map_size: mapSize,
        quartermaster_enabled: quartermaster,
        superior_lenience: lenience,
      });
      await refreshState();
      toast.success("New game forged. Rise.");
      navigate("/command");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed.");
    } finally {
      setBusy(false);
    }
  };

  const fortressCount = mapSize === 31 ? 2 : 1;
  const lenienceLabel = lenience < 0.2 ? "TYRANT"
                       : lenience < 0.45 ? "STRICT"
                       : lenience < 0.55 ? "BALANCED"
                       : lenience < 0.8 ? "LENIENT"
                       : "INDULGENT";

  return (
    <div className="min-h-screen relative">
      <div className="absolute inset-0 z-0">
        <img src={ASSETS.hero} alt="" className="w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0"
             style={{ background: "linear-gradient(180deg, rgba(3,3,3,0.85), rgba(3,3,3,0.95))" }} />
      </div>
      <div className="relative z-10">
        <NavBar active={null} />

        <div className="max-w-[1100px] mx-auto px-4 sm:px-8 lg:px-12 py-10 lg:py-16">
          <button onClick={() => navigate("/command")} className="btn-ghost mb-6 flex items-center gap-1"
                  data-testid="newgame-back-button">
            <ChevronLeft className="w-3 h-3" /> COMMAND
          </button>

          <p className="tac-label fade-up">FORGE A NEW WORLD</p>
          <h1 className="font-serif-h text-5xl lg:text-7xl uppercase text-bone leading-none mt-3 fade-up"
              data-testid="newgame-heading">
            New Game.
          </h1>
          <p className="text-sm text-muted-h max-w-2xl mt-4 fade-up delay-1">
            Set the rules of the dead. These choices stay locked for the run.
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-12">
            {/* Permadeath */}
            <button
              onClick={() => setPermadeath(!permadeath)}
              className={`tactical-card text-left p-6 transition-all fade-up delay-2 ${
                permadeath ? "border-blood bg-[#0F0707]" : ""
              }`}
              data-testid="permadeath-toggle"
            >
              <div className="flex items-start justify-between">
                <Skull className="w-5 h-5 text-blood" />
                <span className={`font-mono text-xs ${permadeath ? "text-blood" : "text-muted-h"}`}>
                  {permadeath ? "PERMADEATH ON" : "CASUAL"}
                </span>
              </div>
              <p className="font-serif-h text-2xl text-bone uppercase mt-4">
                {permadeath ? "True Death" : "The Dead Walk Twice"}
              </p>
              <p className="text-sm text-[var(--text-main)] mt-3 leading-relaxed">
                {permadeath
                  ? "Every fallen zombie stays fallen. Forever. The horde you lose is the horde you can never have again."
                  : "Casual mode. ~50% of fallen zombies trickle back next battle. Less weight, less dread."}
              </p>
              <p className="tac-label mt-4">CLICK TO TOGGLE</p>
            </button>

            {/* Map size */}
            <div className="tactical-card p-6 fade-up delay-3" data-testid="mapsize-panel">
              <div className="flex items-start justify-between">
                <Map className="w-5 h-5 text-blood" />
                <span className="font-mono text-xs text-bone" data-testid="mapsize-value">
                  {mapSize} REGIONS
                </span>
              </div>
              <p className="font-serif-h text-2xl text-bone uppercase mt-4">World Size</p>

              <div className="mt-6">
                <input type="range" min={meta?.min_map_size || 15} max={meta?.max_map_size || 31}
                       value={mapSize} onChange={(e) => setMapSize(Number(e.target.value))}
                       className="blood-slider" data-testid="mapsize-slider" />
                <div className="flex justify-between text-[0.65rem] text-muted-h tracking-widest mt-2">
                  <span>15 SHORT</span>
                  <span>31 GLOBAL</span>
                </div>
              </div>

              <div className="mt-5 space-y-2 text-xs">
                <Row k="REGIONS" v={mapSize} />
                <Row k="FORTRESSES" v={fortressCount} />
                <Row k="POSITION 15" v="KING'S BONE THRONE" hl />
                {mapSize === 31 && <Row k="POSITION 31" v="EMPEROR'S VOID SEAT" hl />}
              </div>
            </div>
          </div>

          {/* Quartermaster (assist) toggle */}
          <button
            onClick={() => setQuartermaster(!quartermaster)}
            className={`tactical-card text-left p-6 transition-all fade-up delay-3 mt-6 w-full ${
              quartermaster ? "border-blood bg-[#0F0707]" : ""
            }`}
            data-testid="quartermaster-toggle"
          >
            <div className="flex items-start justify-between">
              <Shield className="w-5 h-5 text-blood" />
              <span className={`font-mono text-xs ${quartermaster ? "text-blood" : "text-muted-h"}`}>
                {quartermaster ? "QUARTERMASTER ON" : "OFF"}
              </span>
            </div>
            <p className="font-serif-h text-2xl text-bone uppercase mt-4">
              Quartermaster Assist
            </p>
            <p className="text-sm text-[var(--text-main)] mt-3 leading-relaxed">
              {quartermaster
                ? "On the Command page, see the next three upcoming battles with min-horde requirements and expected horde gain. Strategic players' choice."
                : "Pure intuition. No previews. You walk in blind. Recommended for veterans who want the dread."}
            </p>
            <p className="tac-label mt-4">CLICK TO TOGGLE</p>
          </button>

          {/* Superior lenience slider */}
          <div className="tactical-card p-6 fade-up delay-3 mt-6" data-testid="lenience-panel">
            <div className="flex items-start justify-between">
              <Crown className="w-5 h-5 text-blood" />
              <span className="font-mono text-xs text-bone" data-testid="lenience-value">
                {lenienceLabel} ({Math.round(lenience * 100)}%)
              </span>
            </div>
            <p className="font-serif-h text-2xl text-bone uppercase mt-4">Superior's Patience</p>
            <p className="text-sm text-[var(--text-main)] mt-3 leading-relaxed">
              How forgiving your superiors are when you "suggest" attacks below King-rank.
              <span className="text-blood"> Tyrants</span> almost never approve your plans;
              <span className="text-bone"> indulgent</span> ones rubber-stamp anything.
              Affects the world map's suggestion approval rate by ±10%.
            </p>
            <div className="mt-6">
              <input type="range" min={0} max={100} value={Math.round(lenience * 100)}
                     onChange={(e) => setLenience(Number(e.target.value) / 100)}
                     className="blood-slider" data-testid="lenience-slider" />
              <div className="flex justify-between text-[0.65rem] text-muted-h tracking-widest mt-2">
                <span>STRICT</span>
                <span>BALANCED</span>
                <span>LENIENT</span>
              </div>
            </div>
          </div>

          {/* Preview */}
          <div className="mt-8 narrative-panel fade-up delay-3" data-testid="newgame-preview">
            <p className="tac-label mb-3">PREVIEW</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Mini icon={Skull} label="DEATH" value={permadeath ? "PERMANENT" : "RECYCLED 50%"} />
              <Mini icon={Map} label="MAP" value={`${mapSize} REGIONS`} />
              <Mini icon={Crown} label="FORTRESSES" value={fortressCount === 2 ? "KING + EMPEROR" : "KING ONLY"} />
              <Mini icon={Shield} label="DIFFICULTY" value={
                permadeath ? (mapSize >= 28 ? "BRUTAL" : mapSize >= 20 ? "HARD" : "MEDIUM")
                : (mapSize >= 28 ? "MEDIUM" : "EASY")
              } />
            </div>
            <p className="text-xs text-muted-h mt-5 leading-relaxed">
              {fortressCount === 2
                ? "You will face the King at region 15 and the Emperor at region 31. Both fortresses sit on top of human hostages — slaughtering them releases the horde's biggest payouts."
                : "You will face the King at region 15. Choose 31 regions for the Emperor's Final Fortress endgame."}
            </p>
          </div>

          <button onClick={start} disabled={busy} className="btn-blood mt-10" data-testid="newgame-start-button">
            {busy ? <><Loader className="w-3 h-3 animate-spin" /> ...</> : "BEGIN THE HUNT"}
          </button>
        </div>
      </div>
    </div>
  );
}

const Row = ({ k, v, hl }) => (
  <div className="flex justify-between border-b border-subtle pb-1">
    <span className="text-muted-h tracking-wider text-[0.65rem]">{k}</span>
    <span className={`${hl ? "text-blood" : "text-bone"} text-[0.7rem]`}>{v}</span>
  </div>
);

const Mini = ({ icon: Icon, label, value }) => (
  <div className="tactical-card flex items-center gap-2">
    <Icon className="w-4 h-4 text-blood" />
    <div>
      <p className="tac-label">{label}</p>
      <p className="font-mono text-xs text-bone mt-0.5">{value}</p>
    </div>
  </div>
);
